import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Icons,
  useTheme,
  Alert,
} from '@ublend-npm/aulaui-core';
import { useDispatch } from 'react-redux';
import { toggleWidgetPreference } from '@app/actions/home/widgetPreferences';
import { WidgetCard } from '../common';
import OutlookInbox, { OutlookInboxData } from './OutlookInbox';
import OutlookAllEmailsDialog from './OutlookAllEmailsDialog';
import OutlookThreadDialog from './OutlookThreadDialog';
import LinkIcon from '@mui/icons-material/Link';

const inboxData = require('./outlookInbox.json') as OutlookInboxData;

const WIDGET_KEY = 'home-outlook-widget';

type OutlookView =
  | 'connect'
  | 'inbox'
  | 'load-error'
  | 'expired'
  | 'connect-error';

type OutlookErrorStateProps = {
  type: Exclude<OutlookView, 'connect' | 'inbox'>;
  onAction: () => void;
};

const OutlookErrorState = ({ type, onAction }: OutlookErrorStateProps) => {
  const { palette } = useTheme();
  const isDarkMode = palette.mode === 'dark';
  const content = {
    'load-error': {
      title: 'We couldn’t load your emails',
      message: 'Something went wrong while fetching your emails. Try again',
      button: 'Try again',
      background: '#FFF3E0',
      color: '#754500',
      darkBackground: '#FFF4E5',
      darkColor: '#663C00',
      icon: (
        <Alert
          severity="warning"
          variant="standard"
          aria-label="Warning"
          sx={{
            minWidth: 24,
            p: 0,
            bgcolor: 'transparent',
            color: '#F57C00',
            '& .MuiAlert-icon': { m: 0, p: 0 },
            '& .MuiAlert-message, & .MuiAlert-action': { display: 'none' },
          }}
        />
      ),
    },
    expired: {
      title: 'Your connection has expired',
      message: 'Reconnect to continue viewing your outlook emails',
      button: 'Reconnect account',
      background: '#FDEBEC',
      color: '#722525',
      darkBackground: '#1B0909',
      darkColor: '#FFCDD2',
      icon: (
        <Alert
          severity="error"
          variant="standard"
          aria-label="Error"
          sx={{
            minWidth: 22,
            p: 0,
            bgcolor: 'transparent',
            color: '#E53935',
            '& .MuiAlert-icon': { m: 0, p: 0 },
            '& .MuiAlert-message, & .MuiAlert-action': { display: 'none' },
          }}
        />
      ),
    },
    'connect-error': {
      title: 'Unable to connect',
      message: 'We couldn’t connect to Microsoft. Please try again.',
      button: 'Retry',
      background: '#FFF3E0',
      color: '#754500',
      darkBackground: '#1A1200',
      darkColor: '#FFE2B7',
      icon: (
        <Alert
          severity="warning"
          variant="standard"
          aria-label="Warning"
          sx={{
            minWidth: 24,
            p: 0,
            bgcolor: 'transparent',
            color: '#F57C00',
            '& .MuiAlert-icon': { m: 0, p: 0 },
            '& .MuiAlert-message, & .MuiAlert-action': { display: 'none' },
          }}
        />
      ),
    },
  }[type];

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      justifyContent="center"
      flex={1}
      px={1}
      py={2}
    >
      <Box
        display="flex"
        alignItems="flex-start"
        gap={1.5}
        p={2}
        sx={{
          bgcolor: isDarkMode ? content.darkBackground : content.background,
          borderRadius: '4px',
        }}
      >
        {content.icon}
        <Box color={isDarkMode ? content.darkColor : content.color}>
          <Typography variant="subtitle2" fontWeight="bold">
            {content.title}
          </Typography>
          <Typography variant="body2" mt={0.5}>
            {content.message}
          </Typography>
        </Box>
      </Box>

      <Button kind="primary" fullWidth onClick={onAction}>
        {content.button}
      </Button>
    </Box>
  );
};

const OutlookWidget = () => {
  const dispatch = useDispatch<any>();
  const { palette } = useTheme();
  const isDarkMode = palette.mode === 'dark';
  // const [outlookView, setOutlookView] = useState<OutlookView>(() =>
  //   localStorage.getItem('outlook-connected') === 'true' ? 'inbox' : 'connect'
  // );
  const [outlookView, setOutlookView] = useState<OutlookView>('inbox');
  const [isAllEmailsOpen, setIsAllEmailsOpen] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [returnToAllEmails, setReturnToAllEmails] = useState(false);
  const [messages, setMessages] = useState(inboxData.messages);
  const isConnected = outlookView === 'inbox';

  const inboxCount = messages.length;

  const messagesWithAttachmentStatus = useMemo(
    () =>
      messages.map((message) => {
        const thread = inboxData.threads.find(
          (item) => item.id === message.threadId
        );

        return {
          ...message,
          hasAttachment: Boolean(
            thread?.messages.some(
              (threadMessage) => threadMessage.attachments?.length
            )
          ),
        };
      }),
    [messages]
  );

  const displayedMessages = useMemo(
    () => messagesWithAttachmentStatus,
    [messagesWithAttachmentStatus]
  );

  const handleConnect = useCallback(() => {
    const outlookWindow = window.open(
      'https://outlook.office.com/mail/',
      '_blank',
      'noopener,noreferrer'
    );

    if (!outlookWindow) {
      setOutlookView('connect-error');
      return;
    }

    localStorage.setItem('outlook-connected', 'true');
    setOutlookView('inbox');
  }, []);

  const handleViewAll = useCallback(() => {
    setIsAllEmailsOpen(true);
  }, []);

  const handleRefresh = useCallback(() => {
    // UI preview only. Replace this with an inbox fetch once Outlook is connected.
    try {
      setMessages((currentMessages) => [...currentMessages]);
    } catch {
      setOutlookView('load-error');
    }
  }, []);

  const handleRetryLoad = useCallback(() => {
    setOutlookView('inbox');
    handleRefresh();
  }, [handleRefresh]);

  const handleReconnect = useCallback(() => {
    localStorage.removeItem('outlook-connected');
    setOutlookView('connect');
  }, []);

  const handleMessageClick = useCallback(
    (threadId: string, openedFromAllEmails = false) => {
      setIsAllEmailsOpen(false);
      setReturnToAllEmails(openedFromAllEmails);

      setMessages((currentMessages) =>
        currentMessages.map((message) =>
          message.threadId === threadId
            ? { ...message, isUnread: false }
            : message
        )
      );

      setSelectedThreadId(threadId);
    },
    []
  );

  const handleThreadClose = useCallback(() => {
    setSelectedThreadId(null);

    if (returnToAllEmails) {
      setIsAllEmailsOpen(true);
      setReturnToAllEmails(false);
    }
  }, [returnToAllEmails]);

  const handleCompose = useCallback(() => {
    window.open(
      'https://outlook.office.com/mail/deeplink/compose',
      '_blank',
      'noopener,noreferrer'
    );
  }, []);

  const handleHide = useCallback(() => {
    dispatch(toggleWidgetPreference(WIDGET_KEY, true));
  }, [dispatch]);

  const menuActions = useMemo(
    () =>
      isConnected
        ? [
            { label: 'Hide', onClick: handleHide },
            { label: 'Disconnect account', onClick: handleReconnect },
          ]
        : [{ label: 'Hide', onClick: handleHide }],
    [handleHide, handleReconnect]
  );
  const headerAction = (
    <Box
      display="flex"
      alignItems="center"
      gap={{ xs: 0.5, sm: 1.5 }}
      sx={{ flexShrink: 0 }}
    >
      <IconButton
        aria-label="Email options"
        size="small"
        onClick={handleCompose}
        sx={{
          width: 32,
          height: 32,
          minWidth: 32,
          minHeight: 32,
          padding: 0,
          borderRadius: '100px',
          bgcolor: 'surface.mid',
          color: 'primary.main',
          border: '1px solid',
          borderColor: 'primary.main',
          '&:hover': {
            bgcolor: 'surface.mid',
          },
        }}
      >
        <Icons.EditNoteOutlined fontSize="small" />
      </IconButton>
    </Box>
  );
  const inboxHeader = (
    <Box
      display="flex"
      alignItems="center"
      sx={{
        ml: { xs: 0, sm: 1.5 },
        gap: { xs: 0.5, sm: 1 },
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      <img
        src="https://images.seeklogo.com/logo-png/47/2/coventry-university-logo-png_seeklogo-479465.png"
        alt="Coventry University"
        style={{
          height: 56,
          maxHeight: 56,
          width: 'auto',
          objectFit: 'contain',
          display: 'inline-block',
          verticalAlign: 'middle',
          marginRight: 12,
          flexShrink: 0,
        }}
      />

      <Typography
        component="span"
        color="primary"
        sx={{
          fontSize: { xs: '15px', sm: '16px' },
          lineHeight: '24px',
          fontWeight: 500,
          fontFamily: 'Roboto, sans-serif',
        }}
      >
        Inbox
      </Typography>

      {isConnected && inboxCount > 0 ? (
        <Box
          sx={{
            width: 20,
            height: 20,
            minWidth: 20,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {inboxCount}
        </Box>
      ) : null}
    </Box>
  );
  return (
    <>
      <WidgetCard
        title=""
        titleAdornment={inboxHeader}
        headerAction={isConnected ? headerAction : undefined}
        menuActions={menuActions}
        ariaLabel="Outlook widget"
        minHeight="560px"
      >
        {outlookView === 'inbox' ? (
          <OutlookInbox
            messages={displayedMessages}
            onViewAll={handleViewAll}
            onMessageClick={handleMessageClick}
            onRefresh={handleRefresh}
          />
        ) : outlookView === 'load-error' ? (
          <OutlookErrorState type="load-error" onAction={handleRetryLoad} />
        ) : outlookView === 'expired' ? (
          <OutlookErrorState type="expired" onAction={handleReconnect} />
        ) : outlookView === 'connect-error' ? (
          <OutlookErrorState type="connect-error" onAction={handleConnect} />
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            justifyContent="center"
            flex={1}
            px={1}
            py={2}
          >
            <Box
              display="flex"
              gap={1.5}
              p={2}
              sx={{
                bgcolor: isDarkMode ? '#E5F6FD' : '#E1F5FE',
                borderRadius: '4px',
              }}
            >
              <Alert
                severity="info"
                variant="standard"
                aria-label="Information"
                sx={{
                  minWidth: 22,
                  p: 0,
                  bgcolor: 'transparent',
                  color: '#0288D1',
                  '& .MuiAlert-icon': { m: 0, p: 0 },
                  '& .MuiAlert-message, & .MuiAlert-action': {
                    display: 'none',
                  },
                }}
              />

              <Box color="#01579B">
                <Typography variant="subtitle2" fontWeight="bold">
                  Manage your institutional emails.
                </Typography>

                <Typography variant="body2" mt={0.5}>
                  Connect Outlook to stay on top of important emails and
                  streamline communication.
                </Typography>
              </Box>
            </Box>

            <Button
              kind="primary"
              onClick={handleConnect}
              fullWidth
              endIcon={<LinkIcon />}
            >
              Connect your outlook account
            </Button>
          </Box>
        )}
      </WidgetCard>
      <OutlookAllEmailsDialog
        open={isAllEmailsOpen}
        messages={messagesWithAttachmentStatus}
        onClose={() => setIsAllEmailsOpen(false)}
        onMessageClick={(threadId) => handleMessageClick(threadId, true)}
      />
      <OutlookThreadDialog
        open={Boolean(selectedThreadId)}
        thread={inboxData.threads.find(
          (thread) => thread.id === selectedThreadId
        )}
        onClose={handleThreadClose}
      />
    </>
  );
};

export default OutlookWidget;
